import router from 'next/router';

import { queryClient } from '@ping/providers/ReactQueryProvider';
import {
  isBusinessLoginSelector,
  isUserVerifiedSelector,
  useUserInformationStore,
} from '@ping/stores/userInformation.store';

import { destinationPath, keepsKybOnlyPath, shouldConfineBusinessToKyb } from './business-kyb-lock';
import { getKybBusinessStatus, kybBusinessStatusQueryKey } from './kyb-status.api';
import type { IKybBusinessStatus } from './status-screen';

export const BUSINESS_VERIFICATION_PATH = '/business/verification';

const STAYS_AVAILABLE = ['/business/verification', '/user/support', '/login', '/login/callback', '/logout/callback'];

export const keepsCurrentPageDuringBusinessEntry = (pathname: string) => {
  if (pathname === '/kyb' || pathname.startsWith('/kyb/')) {
    return true;
  }

  return STAYS_AVAILABLE.some(path => pathname === path || pathname.startsWith(`${path}/`));
};

export const businessVerificationEntryTarget = (status: string | null) =>
  status === 'Active' ? null : BUSINESS_VERIFICATION_PATH;

export const shouldRedirectIncompleteBusinessLogin = (input: {
  isBusinessLogin: boolean;
  pathname: string;
  status: string | null;
}) => {
  if (!input.isBusinessLogin || keepsCurrentPageDuringBusinessEntry(input.pathname)) {
    return false;
  }

  return businessVerificationEntryTarget(input.status) !== null;
};

export const redirectIncompleteBusinessLogin = () => {
  const unsubscribeStore = useUserInformationStore.sliceSubscribe(isBusinessLoginSelector, isBusinessLogin => {
    if (!isBusinessLogin) {
      return;
    }

    const pathname = window.location.pathname;
    if (keepsCurrentPageDuringBusinessEntry(pathname)) {
      return;
    }

    void getKybBusinessStatus()
      .then(status => status.status)
      .catch(() => null)
      .then(status => {
        if (window.location.pathname !== pathname) {
          return;
        }

        if (shouldRedirectIncompleteBusinessLogin({ isBusinessLogin: true, pathname, status })) {
          void router.replace(BUSINESS_VERIFICATION_PATH);
        }
      });
  });

  const returnToBusinessVerification = () => {
    window.location.replace(BUSINESS_VERIFICATION_PATH);
  };

  const confineBusinessToKyb = (url?: string) => {
    const state = useUserInformationStore.getState();
    const isBusinessLogin = isBusinessLoginSelector(state);
    const isVerified = isUserVerifiedSelector(state);
    const pathname = destinationPath(url || window.location.pathname);
    if (!isBusinessLogin || keepsKybOnlyPath(pathname)) {
      return;
    }

    if (!isVerified) {
      returnToBusinessVerification();
      return;
    }

    const cached = queryClient.getQueryData<IKybBusinessStatus>(kybBusinessStatusQueryKey);
    if (
      shouldConfineBusinessToKyb({
        isBusinessLogin,
        isVerified,
        kybStatus: cached?.status,
        pathname,
      })
    ) {
      if (cached) {
        returnToBusinessVerification();
        return;
      }

      void getKybBusinessStatus()
        .then(status => status.status)
        .catch(() => null)
        .then(kybStatus => {
          if (kybStatus === 'Active' || keepsKybOnlyPath(destinationPath(window.location.pathname))) {
            return;
          }

          returnToBusinessVerification();
        });
    }
  };

  router.events.on('routeChangeStart', confineBusinessToKyb);
  confineBusinessToKyb(window.location.pathname);

  return {
    unsubscribe: () => {
      unsubscribeStore.unsubscribe();
      router.events.off('routeChangeStart', confineBusinessToKyb);
    },
  };
};
